import { useCommunityContext } from 'providers';

import { POST_REPORT_STATUS, POST_STATUSES } from '@jebel/constants';

import { useCurrentUserCommunityMembership } from 'features/community/hooks';
import { HomeFeedPostCreateInput, useHomeFeedPostCreateMutation } from 'shared/graphql';
import { useCurrentUser, useToast } from 'shared/hooks';
import { recordError } from 'shared/utils/record';

/**
 * Create a new `HomeFeedPost` instance.
 *
 * @returns The `create` function and the `loading` state.
 */
export function useHomeFeedPostCreate() {
  const { userId } = useCurrentUser();
  const { selectedCommunity: community } = useCommunityContext();
  const { data: membership } = useCurrentUserCommunityMembership();
  const { showError, showSuccess } = useToast();

  const [mutate, { loading }] = useHomeFeedPostCreateMutation({
    refetchQueries: ['HomeFeedItems'],
  });

  const create = async (data: Omit<HomeFeedPostCreateInput, 'author' | 'schools'>) => {
    try {
      await mutate({
        variables: {
          data: {
            // https://8base-dev.atlassian.net/browse/JEB-1553
            status: POST_STATUSES.pending,
            reportStatus: POST_REPORT_STATUS.pendingReview,
            author: { connect: { id: userId } },

            schools: {
              // Create a connection to the school the user is currently viewing.
              // https://github.com/jebelapp/jebel/issues/1577
              connect: [{ id: community?.id }],
            },

            // https://github.com/jebelapp/jebel/issues/1842
            authorAffiliation: membership?.affiliation ?? data.authorAffiliation,

            ...data,
          },
        },
      });

      showSuccess(
        "Your post will be moderated, we'll let you know when it's available on the feed.",
      );
    } catch (err) {
      recordError(err);
      showError('Something went wrong creating your post, please try again.');

      if (err instanceof Error) {
        showError(err.message);
      }
    }
  };

  return {
    create,
    loading,
  };
}
